import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import SchoolIcon from "@mui/icons-material/School";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import {
  Box,
  Button,
  IconButton,
  keyframes,
  Card,
  CardContent,
  Typography,
  Chip,
  Divider,
  Collapse,
  Tooltip,
  Stack,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import i18next from "i18next";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Game } from "../models";
import { useTheme as useAppTheme } from "../contexts/ThemeContext";
import {
  dateSorter,
  getMyGamesFromLocalStorage,
  isMyTurn,
  whoAmI,
} from "../utils/common";
import { WelcomeView } from "./WelcomeView";
import { CreateGameDialog } from "../components/CreateGameDialog";
import { DialogControl } from "../controls/DialogControl";

export function GameList() {
  const { themeConfig } = useAppTheme();
  const [openGames, setOpenGames] = React.useState<Game[]>();
  const [endedGames, setEndedGames] = React.useState<Game[]>();
  const [createDialogOpen, setCreateDialogOpen] = React.useState(false);
  const [activeGamesExpanded, setActiveGamesExpanded] = React.useState(true);
  const [endedGamesExpanded, setEndedGamesExpanded] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [gameToDelete, setGameToDelete] = React.useState<Game | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  let navigate = useNavigate();

  React.useEffect(() => {
    i18next.loadNamespaces("gameList", (err, t) => {
      if (!err) {
        // Namespace loaded
      } else {
        console.log("ERROR LOADING NS", err);
      }
    });
  }, []);
  const { t } = useTranslation();

  let [count, setCount] = React.useState<number>();

  const initGameList = async () => {
    console.log("📋 initGameList - Loading games");
    setIsLoading(true);

    try {
      // Check if user is logged in
      const { Auth } = await import("aws-amplify");
      let isLoggedIn = false;

      try {
        await Auth.currentAuthenticatedUser();
        isLoggedIn = true;
      } catch {
        isLoggedIn = false;
      }

      if (isLoggedIn) {
        // ✅ LOGGED IN: Query DB by owner/sharedWith
        console.log("📋 Loading games for logged-in user");
        const { listMyGames } = await import("../api/gameApi");
        const games = await listMyGames();

        console.log(`📋 Fetched ${games.length} games from DB`);

        const openGames = games
          .filter((g) => !g.winner)
          .sort(dateSorter("updatedAt", "DESC"));

        const finishedGames = games
          .filter((g) => g.winner)
          .sort(dateSorter("updatedAt", "DESC"));

        setOpenGames(openGames);
        setEndedGames(finishedGames);
        setCount(games.length);

        console.log(
          `✅ Logged in: ${openGames.length} active, ${finishedGames.length} finished`
        );
      } else {
        // ❌ ANONYMOUS: Load from localStorage
        console.log("📋 Loading anonymous games from localStorage");
        const { getAnonymousGameIds } = await import("../utils/anonymousGames");
        const { getGamesByIds } = await import("../api/gameApi");
        const { cleanupFinishedGame } = await import("../utils/anonymousGames");

        const gameIds = getAnonymousGameIds();

        if (gameIds.length === 0) {
          console.log("ℹ️ No anonymous games");
          setOpenGames([]);
          setEndedGames([]);
          setCount(0);
          return;
        }

        console.log(`📋 Fetching ${gameIds.length} anonymous games`);
        const games = await getGamesByIds(gameIds);

        // Only show ACTIVE games for anonymous users
        const activeGames = games
          .filter((g) => !g.winner)
          .sort(dateSorter("updatedAt", "DESC"));

        // Cleanup finished games from localStorage
        const finishedGames = games.filter((g) => g.winner);
        finishedGames.forEach((g) => cleanupFinishedGame(g.id!));

        if (finishedGames.length > 0) {
          console.log(`🧹 Cleaned up ${finishedGames.length} finished games`);
        }

        setOpenGames(activeGames);
        setEndedGames([]); // No history for anonymous users
        setCount(activeGames.length);

        console.log(`✅ Anonymous: ${activeGames.length} active games`);
      }
    } catch (error) {
      console.error("❌ Failed to load games:", error);
      setOpenGames([]);
      setEndedGames([]);
      setCount(0);
    } finally {
      setIsLoading(false);
      console.log("📋 initGameList - Loading complete");
    }
  };

  const deleteGame = async (game: Game) => {
    console.log("🗑️ Deleting game:", game.id);

    try {
      // Delete from DB
      const { deleteGame: apiDeleteGame } = await import("../api/gameApi");
      const success = await apiDeleteGame(game.id!);

      if (success) {
        // Also remove from anonymous games if present
        try {
          const { removeAnonymousGame } = await import(
            "../utils/anonymousGames"
          );
          removeAnonymousGame(game.id!);
        } catch {
          // Not an anonymous game, ignore
        }

        console.log("✅ Game deleted successfully");

        // Reload game list
        initGameList();
      } else {
        console.error("❌ Failed to delete game");
      }
    } catch (error) {
      console.error("❌ Delete game error:", error);
    }
  };

  React.useEffect(() => {
    initGameList();

    // Reload games when window gets focus (user returns to tab)
    const handleFocus = () => {
      console.log("📋 Window focused - reloading game list");
      initGameList();
    };

    // Reload games when cloud sync completes (after login)
    const handleCloudSync = () => {
      console.log("☁️ Cloud sync complete - reloading game list");
      initGameList();
    };

    window.addEventListener("focus", handleFocus);
    window.addEventListener("cloudSyncComplete", handleCloudSync);

    return () => {
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("cloudSyncComplete", handleCloudSync);
    };
  }, []);

  console.log(
    "Count: ",
    count,
    "openGames:",
    openGames?.length,
    "endedGames:",
    endedGames?.length
  );

  // Only show WelcomeView if we've loaded and there are truly no games
  const hasAnyGames =
    (openGames && openGames.length > 0) ||
    (endedGames && endedGames.length > 0);

  // Show WelcomeView only if loading is done (count !== undefined) and no games exist
  if (count === 0 && !hasAnyGames) {
    return (
      <Box m={3}>
        <WelcomeView />
      </Box>
    );
  }
  const blink = keyframes`
  from { opacity: 0.7;  }
  to { opacity: 1; }
`;

  const getTimeAgo = (date: string | null | undefined) => {
    if (!date) return "";
    const now = new Date();
    const then = new Date(date);
    const diffMs = now.getTime() - then.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
      return `${t("gameList:minutesAgo", { count: diffMins })}`;
    } else if (diffHours < 24) {
      return `${t("gameList:hoursAgo", { count: diffHours })}`;
    } else {
      return `${t("gameList:daysAgo", { count: diffDays })}`;
    }
  };

  const handleDeleteClick = (game: Game, e: React.MouseEvent) => {
    e.stopPropagation();
    setGameToDelete(game);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (gameToDelete) {
      await deleteGame(gameToDelete);
      setDeleteDialogOpen(false);
      setGameToDelete(null);
    }
  };

  const getGameCard = (g: Game, index: number) => {
    const myTurn = isMyTurn(g);
    const isWinner = whoAmI(g) === g.winner;
    const hasWinner = !!g.winner;
    const lastUpdate = g.updatedAt || g.createdAt;

    return (
      <Card
        key={`game-${g.id}`}
        elevation={4}
        sx={{
          position: "relative",
          height: 160,
          borderRadius: 2,
          background: themeConfig.dialog.background,
          border: "3px solid",
          borderColor: hasWinner
            ? isWinner
              ? "success.main"
              : "error.main"
            : myTurn
            ? "warning.main"
            : "divider",
          transition: "all 0.2s",
          "&:hover": {
            boxShadow: 3,
            transform: "translateY(-4px)",
            borderColor: hasWinner
              ? isWinner
                ? "success.main"
                : "error.main"
              : "primary.main",
          },
          cursor: "pointer",
        }}
        onClick={() => navigate(`/game/${g.id}`)}
      >
        <CardContent
          sx={{
            p: 2,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            "&:last-child": { pb: 2 },
          }}
        >
          {/* Delete Button - Top Right */}
          <IconButton
            onClick={(e) => handleDeleteClick(g, e)}
            size="small"
            sx={{
              position: "absolute",
              top: 6,
              right: 6,
              p: 0.5,
              color: "text.secondary",
              opacity: 0.5,
              "&:hover": {
                color: "error.main",
                bgcolor: "error.dark",
                opacity: 0.8,
              },
            }}
          >
            <DeleteForeverIcon sx={{ fontSize: 18 }} />
          </IconButton>

          {/* Game Title - Center Top */}
          <Box sx={{ textAlign: "center", mb: 1 }}>
            <Tooltip
              title={g.description || ""}
              arrow
              placement="top"
              disableHoverListener={!g.description}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: "vertical",
                  fontSize: "0.95rem",
                  color: "primary.main",
                  cursor: g.description ? "help" : "default",
                }}
              >
                {g.title}
              </Typography>
            </Tooltip>
            <Divider sx={{ mt: 0.5, borderColor: "divider" }} />
          </Box>

          {/* Players */}
          <Typography
            variant="caption"
            color="text.secondary"
            textAlign="center"
            sx={{ fontSize: "0.75rem", mb: 1 }}
          >
            {g.player1 || "..."} vs. {g.player2 || "..."}
          </Typography>

          {/* Expert Mode Chip */}
          {g.expertMode && (
            <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
              <Chip
                icon={
                  <SchoolIcon sx={{ fontSize: 14, color: "warning.main" }} />
                }
                label={t("gameList:expertMode")}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "0.65rem",
                  bgcolor: "warning.dark",
                  color: "warning.main",
                  border: "1px solid",
                  borderColor: "warning.main",
                  "& .MuiChip-label": { px: 0.75 },
                }}
              />
            </Box>
          )}

          {/* Spacer */}
          <Box sx={{ flex: 1 }} />

          {/* Status Chip - Center Bottom */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
            {hasWinner && (
              <Chip
                icon={
                  isWinner ? (
                    <EmojiEventsIcon
                      sx={{ fontSize: 14, color: "success.main" }}
                    />
                  ) : undefined
                }
                label={isWinner ? t("gameList:youWon") : t("gameList:lost")}
                size="small"
                sx={{
                  height: 22,
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  bgcolor: isWinner ? "success.dark" : "error.dark",
                  color: isWinner ? "success.main" : "error.main",
                  border: "1px solid",
                  borderColor: isWinner ? "success.main" : "error.main",
                  "& .MuiChip-label": { px: 1 },
                }}
              />
            )}
            {!hasWinner && myTurn && (
              <Chip
                label={t("gameList:yourTurn")}
                size="small"
                sx={{
                  height: 22,
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  bgcolor: "warning.dark",
                  color: "warning.main",
                  border: "1px solid",
                  borderColor: "warning.main",
                  "& .MuiChip-label": { px: 1 },
                }}
              />
            )}
            {!hasWinner && !myTurn && (
              <Chip
                label={t("gameList:waiting")}
                size="small"
                sx={{
                  height: 22,
                  fontSize: "0.7rem",
                  bgcolor: "action.hover",
                  color: "text.secondary",
                  border: "1px solid",
                  borderColor: "divider",
                  "& .MuiChip-label": { px: 1 },
                }}
              />
            )}
          </Box>
        </CardContent>

        {/* Footer - Time Info */}
        {lastUpdate && (
          <Box
            sx={{
              borderTop: "1px solid rgba(212, 175, 55, 0.2)",
              px: 1.5,
              py: 0.75,
              bgcolor: "rgba(0, 0, 0, 0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
            }}
          >
            <AccessTimeIcon
              sx={{ fontSize: 11, color: "text.secondary", opacity: 0.6 }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.625rem", lineHeight: 1 }}
            >
              {getTimeAgo(lastUpdate)}
            </Typography>
          </Box>
        )}
      </Card>
    );
  };

  return (
    <Box
      sx={{
        maxWidth: 800,
        mx: "auto",
        p: 3,
      }}
    >
      {/* New Game Button */}
      <Box sx={{ mb: 4, textAlign: "center" }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<AddCircleIcon />}
          onClick={() => setCreateDialogOpen(true)}
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: 3,
            textTransform: "none",
            fontWeight: 700,
            fontSize: "1.1rem",
            bgcolor: "primary.main",
            color: "primary.contrastText",
            border: "2px solid",
            borderColor: "divider",
            boxShadow: 3,
            "&:hover": {
              bgcolor: "primary.dark",
              boxShadow: 4,
              transform: "translateY(-2px)",
            },
            transition: "all 0.2s",
          }}
        >
          {t("gameList:newGameButton")}
        </Button>

        <CreateGameDialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          count={count}
        />
      </Box>

      {/* No Active Games Message */}
      {(!openGames || openGames.length === 0) &&
        endedGames &&
        endedGames.length > 0 && (
          <Box sx={{ mb: 4, width: "100%", textAlign: "center", py: 4 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t("gameList:noActiveGames")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("gameList:createNewGamePrompt")}
            </Typography>
          </Box>
        )}

      {/* Active Games Section */}
      {openGames && openGames.length > 0 && (
        <Box sx={{ mb: 4, width: "100%" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
              cursor: "pointer",
            }}
            onClick={() => setActiveGamesExpanded(!activeGamesExpanded)}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="h5" fontWeight={600}>
                {t("gameList:activeGamesTitle")}
              </Typography>
              <Chip
                label={openGames.length}
                size="small"
                sx={{
                  ml: 1.5,
                  fontWeight: 600,
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                }}
              />
            </Box>
            <IconButton size="small" sx={{ color: "primary.main" }}>
              {activeGamesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Divider sx={{ mb: 2, borderColor: "divider" }} />
          <Collapse in={activeGamesExpanded}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, 1fr)",
                  sm: "repeat(3, 1fr)",
                  md: "repeat(4, 1fr)",
                  lg: "repeat(5, 1fr)",
                },
                gap: 2,
                justifyContent: "center",
              }}
            >
              {openGames.map((g, index) => getGameCard(g, index))}
            </Box>
          </Collapse>
        </Box>
      )}

      {/* Ended Games Section */}
      {endedGames && endedGames.length > 0 && (
        <Box sx={{ width: "100%" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
              cursor: "pointer",
            }}
            onClick={() => setEndedGamesExpanded(!endedGamesExpanded)}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="h5" fontWeight={600}>
                {t("gameList:endedGamesTitle")}
              </Typography>
              <Chip
                label={endedGames.length}
                size="small"
                sx={{
                  ml: 1.5,
                  fontWeight: 600,
                  bgcolor: "rgba(100, 100, 100, 0.3)",
                  color: "rgba(255, 255, 255, 0.7)",
                }}
              />
            </Box>
            <IconButton
              size="small"
              sx={{ color: "text.secondary", opacity: 0.7 }}
            >
              {endedGamesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Divider sx={{ mb: 2, borderColor: "divider" }} />
          <Collapse in={endedGamesExpanded}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, 1fr)",
                  sm: "repeat(3, 1fr)",
                  md: "repeat(4, 1fr)",
                  lg: "repeat(5, 1fr)",
                  xl: "repeat(6, 1fr)",
                },
                gap: 2,
                justifyContent: "center",
              }}
            >
              {endedGames.map((g, index) => getGameCard(g, index))}
            </Box>
          </Collapse>
        </Box>
      )}

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography
            sx={{
              animation: `${blink} 1s linear infinite alternate`,
              color: "text.secondary",
            }}
          >
            {t("gameList:loadingGames")}
          </Typography>
        </Box>
      )}

      {/* Empty States */}
      {!isLoading &&
        (!openGames || openGames.length === 0) &&
        (!endedGames || endedGames.length === 0) && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              {t("gameList:noGamesFound")}
            </Typography>
          </Box>
        )}

      {/* Delete Confirmation Dialog */}
      <DialogControl
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        // dialogProps={{ maxWidth: "xs" }}
        // paperSx={{}}
        title={
          <Typography variant="body1" fontWeight={600}>
            {t("gameList:deleteGameTitle")}
          </Typography>
        }
        actions={
          <Stack direction="row" justifyContent="flex-end" gap={2}>
            <Button onClick={() => setDeleteDialogOpen(false)} color="inherit">
              {t("gameList:cancel")}
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
            >
              {t("gameList:delete")}
            </Button>
          </Stack>
        }
      >
        <Stack>
          <Typography sx={{ mb: 2 }} variant="h4">
            {t("gameList:confirmDelete", { title: gameToDelete?.title || "" })}
          </Typography>

          {/* <Stack direction="row" justifyContent="flex-end" gap={2}>
            <Button onClick={() => setDeleteDialogOpen(false)} color="inherit">
              {t("gameList:cancel")}
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
              autoFocus
            >
              {t("gameList:delete")}
            </Button>
          </Stack> */}
        </Stack>
      </DialogControl>
    </Box>
  );
}
